import type {SVGProps} from "react"

import {BRAND} from "./brand"
import {
    GLYPH_TRANSFORM,
    MARK_GLYPH_PATH,
    MARK_GLYPH_VIEWBOX,
    WORDMARK_PATH,
    WORDMARK_VIEWBOX,
} from "./paths"

/**
 * Inline SVG, like the Agenta marks these replace: packages ship no assets, and `/m`'s basePath
 * breaks bare `/assets/...` paths.
 */
export interface BrandMarkProps extends SVGProps<SVGSVGElement> {
    /** Kept for drop-in compatibility with the old `AgentaMark` API; recolors the glyph. */
    markClassName?: string
}

/** 「法」 on the lime tile — the official favicon. Square; fixed colors on both themes. */
export const BrandMark = ({markClassName, ...props}: BrandMarkProps) => (
    <svg viewBox="0 0 64 64" role="img" aria-label={BRAND.name["zh-TW"]} {...props}>
        <rect width="64" height="64" rx="14" fill={BRAND.colors.accent} />
        <svg x="7" y="7" width="50" height="50" viewBox={MARK_GLYPH_VIEWBOX}>
            <g transform={GLYPH_TRANSFORM}>
                <path
                    d={MARK_GLYPH_PATH}
                    className={markClassName}
                    fill={markClassName ? undefined : BRAND.colors.ink}
                />
            </g>
        </svg>
    </svg>
)

/** The calligraphic wordmark alone. Ratio 8:1 — set a height, the width follows. */
export const BrandWordmark = ({markClassName, ...props}: BrandMarkProps) => (
    <svg
        viewBox={WORDMARK_VIEWBOX}
        role="img"
        aria-label={BRAND.name["zh-TW"]}
        fill="currentColor"
        {...props}
    >
        <g transform={GLYPH_TRANSFORM}>
            <path d={WORDMARK_PATH} className={markClassName} />
        </g>
    </svg>
)

export interface BrandLockupProps {
    /** Height of the wordmark row, e.g. "h-6"; the caption scales with it via em. */
    className?: string
    /** Wordmark height in px; the caption is set at 0.32× of it. */
    size?: number
}

/** Wordmark with TAIWAN LEGAL TECH PLATFORM beneath it. */
export const BrandLockup = ({className, size = 24}: BrandLockupProps) => (
    <span
        role="img"
        aria-label={`${BRAND.name["zh-TW"]} ${BRAND.caption}`}
        className={["inline-flex flex-col items-start", className].filter(Boolean).join(" ")}
        style={{gap: Math.round(size * 0.18)}}
    >
        <BrandWordmark aria-hidden role="presentation" height={size} width={size * 8} />
        <span
            aria-hidden
            style={{
                fontSize: Math.max(8, Math.round(size * 0.32)),
                letterSpacing: "0.28em",
                lineHeight: 1,
                whiteSpace: "nowrap",
            }}
            className="font-medium uppercase opacity-70"
        >
            {BRAND.caption}
        </span>
    </span>
)
